import { CommonModule } from '@angular/common';
import {
  Component,
  ChangeDetectorRef,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { combineLatest, filter, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators'; // ← AGGIUNGI questo import

import { SidebarDataService, SidebarOption } from 'src/app/services/sidebar-data.service';
import { StateManagementService } from 'src/app/services/state-management.service';
import { State } from 'src/app/interface/interface';
import { phaseOfWorkState, normalizeWorkState } from 'src/app/shared/work-state';

type KpiPhase =
  | 'ALL'
  | 'DA_AVVIARE'
  | 'IN_PROGETTAZIONE'
  | 'IN_ESECUZIONE'
  | 'IN_VERIFICA'
  | 'CONNESSI'
  | 'COLLAUDATI';

@Component({
  selector: 'app-card-info',
  templateUrl: './card-info.component.html',
  styleUrls: ['./card-info.component.css'],
  standalone: true,
  imports: [CommonModule],
})
export class CardInfoComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();

  // ========= UI (chip) as id+label =========
  selectedRegionUi: SidebarOption[] = [];
  selectedProvinceUi: SidebarOption[] = [];
  selectedEnteUi: SidebarOption[] = [];
  selectedReteUi: SidebarOption[] = [];

  // ========= extra UI state =========
  selectedPhase: string = '';
  isCriticitaOpen = false;

  showAllRegions = false;
  showProvinceDropdown = false;
  showEnteDropdown = false;
  showReteDropdown = false;

  // ========= KPI =========
  isLoadingCards = true;
  totalSites: number | null = null;

  daAvviare = 0;
  inProgettazione = 0;
  inEsecuzione = 0;
  inVerifica = 0;
  connessi = 0;
  collaudati = 0;

  criticitaAlta = 0;
  criticitaMedia = 0;
  criticitaBassa = 0;

  currentDateWhere = '';

  // ========= accordion =========
  isAccordionOpen = true;
  userToggled = false;

  constructor(
    private sidebarData: SidebarDataService,
    private stateService: StateManagementService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // 1) KPI sempre su siti filtrati - ORA CON GESTIONE CORRETTA DEL LOADING
    this.subscription.add(
      combineLatest([
        this.sidebarData.filteredSites$,
        this.sidebarData.loading$
      ]).pipe(
        filter(([siti, loading]) => !loading)
      ).subscribe(([siti, loading]) => {        
        this.isLoadingCards = false;
        this.totalSites = siti.length;
        this.calculateWorkStates(siti);
        this.cdr.detectChanges();
      })
    );

    // 2) Chip id+label - ORA CON VALIDAZIONE REGIONI
    this.subscription.add(
      combineLatest([
        this.stateService.state$, 
        this.sidebarData.sidebarData$
      ]).pipe(
        // Attendi che almeno le regioni siano caricate
        filter(([st, opts]) => 
          !!opts && 
          Array.isArray(opts.regioni) && 
          opts.regioni.length > 0
        ),
      ).subscribe(([st, opts]) => {
        this.selectedRegionUi = this.mergeSelectedWithOptions(
          st.selectedRegion ?? [], 
          opts.regioni
        );
        this.selectedProvinceUi = this.mergeSelectedWithOptions(
          st.selectedProvince ?? [], 
          opts.province
        );
        this.selectedEnteUi = this.mergeSelectedWithOptions(
          st.selectedEnte ?? [], 
          opts.enti
        );
        this.selectedReteUi = this.mergeSelectedWithOptions(
          st.selectedRete ?? [], 
          opts.reti
        );

        this.cdr.markForCheck();
      })
    );

    // 3) resetAll: pulisci phase + KPI
    this.subscription.add(
      this.stateService.getResetAll$().subscribe(() => {
        this.selectedPhase = '';
        this.isCriticitaOpen = false;
        this.sidebarData.clearGlobalKpiFilter();
        this.cdr.markForCheck();
      })
    );

    this.checkViewport();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  // ======================
  // UI / accordion
  // ======================
  toggleAccordion(): void {
    this.userToggled = true;
    this.isAccordionOpen = !this.isAccordionOpen;
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkViewport();
  }

  private checkViewport(): void {
    const isMobile = window.innerWidth < 768;
    if (!this.userToggled) this.isAccordionOpen = !isMobile;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.showAllRegions = false;
    this.showProvinceDropdown = false;
    this.showEnteDropdown = false;
    this.showReteDropdown = false;
    this.isCriticitaOpen = false;
  }

  toggleRegionsDropdown(ev?: MouseEvent): void {
    ev?.stopPropagation();
    this.showAllRegions = !this.showAllRegions;
    this.showProvinceDropdown = false;
    this.showEnteDropdown = false;
    this.showReteDropdown = false;
  }

  toggleProvinceDropdown(ev?: MouseEvent): void {
    ev?.stopPropagation();
    this.showProvinceDropdown = !this.showProvinceDropdown;
    this.showAllRegions = false;
    this.showEnteDropdown = false;
    this.showReteDropdown = false;
  }

  toggleEnteDropdown(ev?: MouseEvent): void {
    ev?.stopPropagation();
    this.showEnteDropdown = !this.showEnteDropdown;
    this.showAllRegions = false;
    this.showProvinceDropdown = false;
    this.showReteDropdown = false;
  }

  toggleReteDropdown(ev?: MouseEvent): void {
    ev?.stopPropagation();
    this.showReteDropdown = !this.showReteDropdown;
    this.showAllRegions = false;
    this.showProvinceDropdown = false;
    this.showEnteDropdown = false;
  }

  // ======================
  // Chips remove (update global state)
  // ======================
  removeRegion(opt: SidebarOption): void {
    const st = this.stateService.state$.value as State;
    const next = (st.selectedRegion ?? []).filter((id) => String(id) !== String(opt.id));
    this.stateService.setRegioni(next);
  }

  removeProvince(opt: SidebarOption): void {
    const st = this.stateService.state$.value as State;
    const next = (st.selectedProvince ?? []).filter((id) => String(id) !== String(opt.id));
    this.stateService.setProvince(next);
  }

  removeEnte(opt: SidebarOption): void {
    const st = this.stateService.state$.value as State;
    const next = (st.selectedEnte ?? []).filter((id) => String(id) !== String(opt.id));
    this.stateService.setEnti(next);
  }

  removeRete(opt: SidebarOption): void {
    const st = this.stateService.state$.value as State;
    const next = (st.selectedRete ?? []).filter((id) => String(id) !== String(opt.id));
    this.stateService.setReti(next);
  }

  resetAll(): void {
    this.selectedPhase = '';
    this.stateService.resetAll();
  }

  // ======================
  // Phase chip + KPI click
  // ======================
  toggleFilterByPhase(phase: KpiPhase): void {
    if (phase === 'ALL' || this.selectedPhase === phase) {
      this.selectedPhase = '';
      this.sidebarData.clearGlobalKpiFilter();
      return;
    }

    this.selectedPhase = phase;
    this.sidebarData.setGlobalPhase(
      phase as 'DA_AVVIARE' | 'IN_PROGETTAZIONE' | 'IN_ESECUZIONE' | 'IN_VERIFICA' | 'CONNESSI' | 'COLLAUDATI'
    );
  }

  removePhase(): void {
    this.selectedPhase = '';
    this.sidebarData.clearGlobalKpiFilter();
  }

  toggleCriticita(ev?: MouseEvent): void {
    ev?.stopPropagation();
    this.isCriticitaOpen = !this.isCriticitaOpen;
  }

  filterByCriticita(level: 'alta' | 'media' | 'bassa', ev?: MouseEvent): void {
    ev?.preventDefault();
    ev?.stopPropagation();

    this.selectedPhase = `CRITICITA_${level.toUpperCase()}`;
    this.sidebarData.setGlobalCriticita(level);
    this.isCriticitaOpen = false;
  }

  private mergeSelectedWithOptions(selectedIds: string[], options: SidebarOption[]): SidebarOption[] {
    const byId = new Map(options.map(o => [String(o.id), o]));
    return (selectedIds ?? []).map(id => byId.get(String(id)) ?? ({ id: String(id), label: String(id) }));
  }

  // ======================
  // KPI compute (client-side)
  // ======================
  private calculateWorkStates(siti: any[]): void {
    this.daAvviare = 0;
    this.inProgettazione = 0;
    this.inEsecuzione = 0;
    this.inVerifica = 0;
    this.connessi = 0;
    this.collaudati = 0;

    this.criticitaAlta = 0;
    this.criticitaMedia = 0;
    this.criticitaBassa = 0;

    for (const it of siti) {
      const a = it?.attributes ?? it;

      // Conteggio KPI per fase (mappatura centralizzata in shared/work-state.ts)
      switch (phaseOfWorkState(a.wr_work_state)) {
        case 'DA_AVVIARE':      this.daAvviare++;      break;
        case 'IN_PROGETTAZIONE': this.inProgettazione++; break;
        case 'IN_ESECUZIONE':   this.inEsecuzione++;   break;
        case 'IN_VERIFICA':     this.inVerifica++;     break;
        case 'CONNESSI':        this.connessi++;       break;
        case 'COLLAUDATI':      this.collaudati++;     break;
      }

      // Criticità: invariata (dipende da wr_work_state === 'criticità' + wr_critical,
      // oggi non alimentati da Wrike -> resta a 0 finché la sorgente non li popola).
      if (normalizeWorkState(a.wr_work_state) === 'criticità') {
        const critical = normalizeWorkState(a.wr_critical);
        if (critical === 'alta') this.criticitaAlta++;
        else if (critical === 'media') this.criticitaMedia++;
        else if (critical === 'bassa') this.criticitaBassa++;
      }
    }
  }

  getCriticitaPercentages() {
    const total = this.criticitaAlta + this.criticitaMedia + this.criticitaBassa || 1;
    return {
      alta: Math.round((this.criticitaAlta / total) * 100),
      media: Math.round((this.criticitaMedia / total) * 100),
      bassa: Math.round((this.criticitaBassa / total) * 100),
    };
  }
}